import qs from 'qs'

import { assert } from 'core/helpers/assert'
import * as R from 'core/helpers/remeda'

/**
 * Returns an error object that can be thrown, after doing its best to glean
 * information from the response text to add to the error object.
 *
 * @param response - The response object. Make sure its stream hasn't already
 * been read yet by .text() or .json().
 */
export const parseErrorResponse = async (response: Response) => {
  const httpStatus = response?.status ?? 0
  const peachRequestId = response.headers.get('X-Peach-Request-Id') || undefined
  let message = 'Unknown Error' // Default message
  let additionalErrorProps = {}

  try {
    const responseText = await response.text()

    if (responseText) {
      // Only proceed if there's text content
      try {
        const jsonData = JSON.parse(responseText)

        if (
          typeof jsonData === 'object' &&
          jsonData !== null &&
          !Array.isArray(jsonData)
        ) {
          // JSON object
          additionalErrorProps = R.omit(jsonData, ['message', 'status']) // Capture other properties
          if (typeof jsonData.message === 'string' && jsonData.message) {
            message = jsonData.message
          } else {
            // Fallback to full text if specific .message is not a good string, or not present
            message = responseText
          }
        } else if (typeof jsonData === 'string') {
          // JSON-encoded string
          message = jsonData
        } else {
          // Body was valid JSON but not an object or string (e.g. number, boolean, array).
          // Use the raw text.
          message = responseText
        }
      } catch (_jsonParseError) {
        // Not JSON, or JSON.parse failed. Use the raw text as the message.
        message = responseText
      }
    }
    // If responseText was empty, message remains 'Unknown Error'.
  } catch (_textReadError) {
    // Failed to read response.text(). Message remains 'Unknown Error'.
  }

  return {
    status: httpStatus,
    message,
    peachRequestId,
    ...additionalErrorProps, // Spread other properties from JSON object error
  }
}

export type FetchOptions = Omit<RequestInit, 'body'> & {
  url: string
  query?: { [index: string]: any }
  body?: unknown
  withHeaders?: boolean
}

/**
 * This is wrapper around `window.fetch` (but that could be subbed out for
 * something else later) that
 *
 * - returns a promise
 * - resolves to a value
 * - rejects with an error with at least `status` and `message` fields
 * - Parses a `query` option if passed in
 * - Adds default headers
 * - JSON.stringifies body if an object is passed
 * - Gracefully handle 204
 * - Parses JSON, text, and pdf responses
 * - Parses JSON error
 *
 * Note: does not handle authorization or unauthorized handling. That's one level up in peachApi.js
 */
const rawApiCall = async ({
  url,
  headers,
  body,
  query,
  method,
  withHeaders,
  ...restOptions
}: FetchOptions) => {
  assert(R.isString(url), 'url is required for an API call')

  const fetchOptions: RequestInit = {
    method,
    headers: {
      Accept: 'application/json',
      ...headers,
    },
    ...restOptions,
  }

  if (!(body instanceof FormData)) {
    fetchOptions.headers!['Content-Type'] = 'application/json'
  }

  if (body instanceof FormData) {
    fetchOptions.body = body
  } else if (!R.isNil(body)) {
    fetchOptions.body = R.isString(body) ? body : JSON.stringify(body)
  }

  const resp = await window.fetch(
    url + qs.stringify(query, { addQueryPrefix: true, arrayFormat: 'comma' }),
    fetchOptions,
  )

  if (resp.ok) {
    const contentType = resp.headers.get('Content-Type')
    return resp.status === 204 && withHeaders
      ? R.pick(resp, ['headers', 'status'])
      : resp.status === 204
      ? {}
      : contentType?.includes('json')
      ? resp.json()
      : contentType?.includes('text')
      ? resp.text()
      : contentType?.includes('pdf')
      ? resp.blob()
      : {}
  } else {
    throw await parseErrorResponse(resp)
  }
}

export default rawApiCall
